import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type TimeRangeOption = {
  value: string;
  label: string;
};

/**
 * CMP-style time-range dropdown: dark surface, brand-blue open border,
 * blue selected row, muted hover.
 */
export function TimeRangeFilter({
  label,
  detail,
  value,
  options,
  onChange,
  ariaLabel = 'Change date range',
  className,
  contentClassName,
  size = 'default',
  title,
}: {
  label: string;
  detail: string;
  value: string;
  options: readonly TimeRangeOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
  contentClassName?: string;
  /** `compact` matches Figma chart-header Navigation (~49px). */
  size?: 'default' | 'compact';
  /** Native hover tooltip — use for honest semantics that don't fit the subtitle. */
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const compact = size === 'compact';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          {...(title ? { title } : {})}
          className={cn(
            'flex w-[280px] items-center justify-between border bg-[#101010] px-3 text-left outline-none transition-colors',
            compact ? 'h-[49px]' : 'h-[62px]',
            open
              ? 'rounded-t-lg border border-b-0 border-[#0194F5]'
              : 'rounded-lg border border-[#272729] hover:border-[#504F4E]',
            className,
          )}
        >
          <span className="min-w-0">
            <span
              className={cn(
                'block truncate font-normal text-white',
                compact ? 'text-sm leading-5' : 'text-base leading-6',
              )}
            >
              {label}
            </span>
            <span className="block truncate text-xs font-light leading-[14px] text-[#D1D5DB]">
              {detail}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'ml-2 shrink-0 text-white transition-transform',
              compact ? 'size-4' : 'size-5',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={0}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] rounded-t-none rounded-b-lg border border-t-0 border-[#0194F5] bg-[#101010] p-2 shadow-none data-[state=open]:animate-none',
          contentClassName,
        )}
      >
        <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          {options.map((opt) => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  'h-11 w-full rounded px-3 text-left text-base font-normal leading-6 transition-colors',
                  selected
                    ? 'bg-[#0194F5] text-white'
                    : 'text-[#D1D5DB] hover:bg-[#272729] hover:text-white',
                )}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
